import { Typography } from '@euler/app/components/typography';
import { kApdmCampaignStatusLabels } from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  AiHomeActivity,
  AiHomeCampaignActivity,
  AiHomeFeature,
} from '@euler/model/ai-home';
import { ApdmCampaignStatus } from '@euler/model/apdm';
import { memo, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { campaignStatusColor } from '../chat/AiCampaignMonitor';
import { aiColors, aiSuiteToolForFeature, tint } from '../theme';

const kApdmTool = aiSuiteToolForFeature(AiHomeFeature.Apdm);

/**
 * What the AI is running for this person right now, above the tools: the
 * home page answers "what is going on" before "what do you want to do".
 * Hidden when nothing runs.
 */
export const AiRecentActivity = memo(
  ({
    items,
    more,
    wide,
    onOpen,
    onMore,
  }: {
    items: AiHomeActivity[];
    /** There is more than shown. */
    more: boolean;
    /** Two columns on a wide screen. */
    wide: boolean;
    onOpen: (item: AiHomeActivity) => void;
    onMore: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    if (!items.length) return null;
    return (
      <View>
        <View style={styles.header}>
          <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
            最近动态
          </Typography.Text>
          {more ? (
            <Pressable accessibilityRole="button" onPress={onMore} hitSlop={8}>
              <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
                查看全部
              </Typography.Text>
            </Pressable>
          ) : null}
        </View>
        <View style={styles.grid}>
          {items.map(item => (
            <CampaignTile
              key={item.campaignId}
              item={item}
              wide={wide}
              onPress={() => onOpen(item)}
            />
          ))}
        </View>
      </View>
    );
  },
);

/** The numbers worth a glance, shown even at zero. */
function highlights(
  item: AiHomeCampaignActivity,
): Array<{ text: string; color: string }> {
  return [
    { text: `新线索 ${item.newLeads}`, color: aiColors.green },
    { text: `回复 ${item.replied}`, color: aiColors.primary },
  ];
}

const CampaignTile = memo(
  ({
    item,
    wide,
    onPress,
  }: {
    item: AiHomeCampaignActivity;
    wide: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const color = campaignStatusColor(item.status);
    const share = item.audience
      ? Math.min(1, item.attempted / item.audience)
      : 0;
    const action =
      item.status === ApdmCampaignStatus.Paused ? '继续' : '查看进度';
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={item.title}
        onPress={onPress}
        style={({ pressed }) => [
          styles.tile,
          wide && styles.tileWide,
          pressed && { opacity: 0.8 },
        ]}
      >
        <View style={styles.titleRow}>
          <Typography.Text
            medium
            size={rpx(14)}
            color={aiColors.foreground}
            numberOfLines={2}
            style={{ flex: 1 }}
          >
            {item.title}
          </Typography.Text>
          <View style={[styles.status, { backgroundColor: tint(color, 0.12) }]}>
            <View style={[styles.dot, { backgroundColor: color }]} />
            <Typography.Text medium size={rpx(11)} color={color}>
              {kApdmCampaignStatusLabels[item.status]}
            </Typography.Text>
          </View>
        </View>
        <View>
          <View style={styles.progressRow}>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              已发送 {item.attempted.toLocaleString('zh-CN')}/
              {item.audience.toLocaleString('zh-CN')}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {Math.round(share * 100)}%
            </Typography.Text>
          </View>
          <View style={styles.track}>
            <View
              style={[
                styles.fill,
                { width: `${share * 100}%`, backgroundColor: color },
              ]}
            />
          </View>
        </View>
        <View style={styles.footer}>
          <View style={styles.highlights}>
            {highlights(item).map(chip => (
              <View
                key={chip.text}
                style={[
                  styles.highlight,
                  { backgroundColor: tint(chip.color, 0.1) },
                ]}
              >
                <Typography.Text medium size={rpx(11)} color={chip.color}>
                  {chip.text}
                </Typography.Text>
              </View>
            ))}
          </View>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {kApdmTool?.title} · {action}
          </Typography.Text>
        </View>
      </Pressable>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: rpx(22),
          marginBottom: rpx(12),
        },
        grid: { flexDirection: 'row', flexWrap: 'wrap', gap: rpx(10) },
        tile: {
          width: '100%',
          gap: rpx(12),
          padding: rpx(14),
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          shadowColor: '#0F172A',
          shadowOpacity: 0.1,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 3 },
          elevation: 3,
        },
        tileWide: { width: undefined, flexBasis: '48%', flexGrow: 0 },
        titleRow: {
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: rpx(8),
        },
        status: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(2),
          borderRadius: rpx(10),
        },
        dot: { width: rpx(6), height: rpx(6), borderRadius: rpx(3) },
        progressRow: {
          flexDirection: 'row',
          justifyContent: 'space-between',
        },
        track: {
          marginTop: rpx(6),
          height: rpx(6),
          borderRadius: rpx(3),
          backgroundColor: aiColors.muted,
          overflow: 'hidden',
        },
        fill: { height: '100%', borderRadius: rpx(3) },
        footer: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        highlights: { flexDirection: 'row', gap: rpx(6) },
        highlight: {
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(4),
          borderRadius: rpx(8),
        },
      }),
    [rpx],
  );
}
